import { Button, CheckBox } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { H2 } from '@ui';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import {
    DELETE_DEBIT_CARD,
    SELECT_REPAYMENT_ROLE,
    UPDATE_DEBIT_CARD
} from './EditPaymentMethodModal.actions';
import { CLOSE_MODAL } from '../../../iwModals/Modals.actions';
import { CancelAction } from '../../ui/confirmationResponse/confirmationQuestion/cancelAction/CancelAction';
import { ConfirmAction } from '../../ui/confirmationResponse/confirmationQuestion/confirmAction/ConfirmAction';
import { ConfirmationQuestion } from '../../ui/confirmationResponse/confirmationQuestion/ConfirmationQuestion';
import { ConfirmationResponse } from '../../ui/confirmationResponse/ConfirmationResponse';
import { ConfirmationStatement } from '../../ui/confirmationResponse/confirmationStatement/ConfirmationStatement';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    closeModal: () => dispatch({ type: CLOSE_MODAL }),
    deleteDebitCard: (id: string) =>
        dispatch({ type: DELETE_DEBIT_CARD, paymentMethodID: id }),
    updateDebitCard: (debitCardData: {
        paymentMethodID: string;
        repaymentRole: string | null;
        fundingRole: string;
    }) => dispatch({ type: UPDATE_DEBIT_CARD, ...debitCardData }),
    selectRepaymentRole: (repaymentRole: string | null) =>
        dispatch({ type: SELECT_REPAYMENT_ROLE, repaymentRole })
});

const mapStateToProps = ({
    editPaymentMethodModal
}: {
    editPaymentMethodModal: RootState['editPaymentMethodModal'];
}) => ({
    ...editPaymentMethodModal
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

function EditDebitCard(props: PropsFromRedux) {
    const {
        deleteDebitCard,
        selectRepaymentRole,
        paymentMethodID,
        repaymentRole,
        fundingRole,
        updateDebitCard,
        closeModal,
        updatingDebitCard,
        cardNumber
    } = props;

    const handleDelete = () => {
        deleteDebitCard(paymentMethodID!);
    };

    const handleUpdateDebitCard = () => {
        updateDebitCard({
            paymentMethodID: paymentMethodID!,
            repaymentRole: repaymentRole,
            fundingRole: fundingRole!
        });
    };

    return (
        <div data-testid="Edit-Payment-Modal">
            <H2 className="mb-xl pt-s">
                <FormattedMessage
                    id="_.edit_debit_card_title"
                    defaultMessage="Card ●●●● {cardNumber}"
                    values={{
                        cardNumber: cardNumber?.slice(cardNumber.length - 4)
                    }}
                />
            </H2>

            <div className="mb-xl">
                <CheckBox
                    label="Use as default payment method for automatic repayments"
                    checked={repaymentRole === 'primary'}
                    onChange={(e) =>
                        selectRepaymentRole(e.target.checked ? 'primary' : null)
                    }
                />
            </div>

            <ConfirmationResponse className="mb-xl" onSubmit={handleDelete}>
                <ConfirmationStatement>
                    <FormattedMessage
                        id="_.edit_debit_card.delete_payment_method"
                        defaultMessage="Delete payment method"
                    />
                </ConfirmationStatement>

                <ConfirmationQuestion>
                    <FormattedMessage
                        id="_.edit_debit_card.delete_confirmation"
                        defaultMessage="Are you sure?"
                    />

                    <ConfirmAction>
                        <FormattedMessage
                            id="_.edit_debit_card.confirm_action"
                            defaultMessage="Yes"
                        />
                    </ConfirmAction>

                    <CancelAction>
                        <FormattedMessage
                            id="_.edit_debit_card.cancel_action"
                            defaultMessage="No"
                        />
                    </CancelAction>
                </ConfirmationQuestion>
            </ConfirmationResponse>

            <div className="flex justify-between">
                <Button onClick={closeModal} variant="tertiary">
                    <FormattedMessage
                        id="_.your_company.prompt.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>

                <Button
                    onClick={handleUpdateDebitCard}
                    loading={updatingDebitCard}
                >
                    <FormattedMessage
                        id="_.your_company.prompt.save"
                        defaultMessage="Save"
                    />
                </Button>
            </div>
        </div>
    );
}

export const EditDebitCardForm = connect(
    mapStateToProps,
    mapDispatchToProps
)(EditDebitCard);
